// ---------------------------------------------------------------------------
// Font Pairing Registry
// ---------------------------------------------------------------------------

export interface FontPairing {
  key: string;
  label: string;
  /** CSS font-family string for headings */
  primary: string;
  /** CSS font-family string for body text */
  secondary: string;
}

export const FONT_PAIRINGS: Record<string, FontPairing> = {
  "noto-serif-manrope": {
    key: "noto-serif-manrope",
    label: "Noto Serif + Manrope",
    primary: "'Noto Serif', Georgia, serif",
    secondary: "'Manrope', sans-serif",
  },
  "inter-system": {
    key: "inter-system",
    label: "Inter + System UI",
    primary: "'Inter', system-ui, -apple-system, sans-serif",
    secondary: "'Inter', system-ui, -apple-system, sans-serif",
  },
  "manrope-inter": {
    key: "manrope-inter",
    label: "Manrope + Inter",
    primary: "'Manrope', sans-serif",
    secondary: "'Inter', system-ui, sans-serif",
  },
  "dm-sans-inter": {
    key: "dm-sans-inter",
    label: "DM Sans + Inter",
    primary: "'DM Sans', sans-serif",
    secondary: "'Inter', system-ui, sans-serif",
  },
  "plus-jakarta-inter": {
    key: "plus-jakarta-inter",
    label: "Plus Jakarta Sans + Inter",
    primary: "'Plus Jakarta Sans', sans-serif",
    secondary: "'Inter', system-ui, sans-serif",
  },
  "outfit-inter": {
    key: "outfit-inter",
    label: "Outfit + Inter",
    primary: "'Outfit', sans-serif",
    secondary: "'Inter', system-ui, sans-serif",
  },
  // Agent Directory default (spec §4 v1 carry-over). Geist Sans is bound in
  // apps/agentdirectory/src/app/layout.tsx via next/font/google as the CSS var
  // --font-geist-sans (used for both headings and body). Geist Mono
  // (--font-geist-mono) is the companion for code/data surfaces and is not part
  // of this heading/body pairing.
  geist: {
    key: "geist",
    label: "Geist",
    primary: "var(--font-geist-sans)",
    secondary: "var(--font-geist-sans)",
  },
};

/**
 * Maps legacy font pairing keys (stored in DB before migration) to new keys.
 */
const LEGACY_KEY_MAP: Record<string, string> = {
  default: "noto-serif-manrope",
  modern: "manrope-inter",
  classic: "outfit-inter",
};

export function getFontPairing(key: string): FontPairing {
  const resolvedKey = LEGACY_KEY_MAP[key] || key;
  return FONT_PAIRINGS[resolvedKey] || FONT_PAIRINGS["noto-serif-manrope"];
}

// ---------------------------------------------------------------------------
// Brand Theme Config
// ---------------------------------------------------------------------------

export interface BrandThemeConfig {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  backgroundColor?: string | null;
  accentColor?: string | null;
  textColor?: string | null;
  sectionBgColor?: string | null;
  overlineColor?: string | null;
  /** Direct CSS font-family string for headings */
  fontHeading?: string | null;
  /** Direct CSS font-family string for body text */
  fontBody?: string | null;
  /** Key from FONT_PAIRINGS registry — used when fontHeading/fontBody are not set */
  fontPairing?: string | null;
}

export const BRAND_DEFAULTS = {
  primaryColor: "#014F8A",
  secondaryColor: "#ffdf20",
  backgroundColor: "#FFFFFF",
  accentColor: "#C9A84C",
  textColor: "#1A1A1A",
  sectionBgColor: "#f4f2ef",
  overlineColor: "#745b00",
} as const;

// ---------------------------------------------------------------------------
// CSS Generation
// ---------------------------------------------------------------------------

/**
 * Build a CSS string that sets `--brand-*` custom properties on `:root`.
 * Includes derived tokens via `color-mix()`.
 */
export function buildBrandCSS(config: BrandThemeConfig): string {
  const primary = config.primaryColor || BRAND_DEFAULTS.primaryColor;
  const secondary = config.secondaryColor || BRAND_DEFAULTS.secondaryColor;
  const background = config.backgroundColor || BRAND_DEFAULTS.backgroundColor;
  const accent = config.accentColor || BRAND_DEFAULTS.accentColor;
  const text = config.textColor || BRAND_DEFAULTS.textColor;
  const sectionBg = config.sectionBgColor || BRAND_DEFAULTS.sectionBgColor;
  const overline = config.overlineColor || BRAND_DEFAULTS.overlineColor;

  // Resolve fonts: direct values take precedence over font pairing key
  const pairing = getFontPairing(config.fontPairing || "noto-serif-manrope");
  const fontHeading = config.fontHeading || pairing.primary;
  const fontBody = config.fontBody || pairing.secondary;

  const declarations = [
    `--brand-primary: ${primary};`,
    `--brand-primary-text: ${readableThemeColor(primary, background, 5)};`,
    `--brand-secondary: ${secondary};`,
    `--brand-background: ${background};`,
    `--brand-accent: ${accent};`,
    `--brand-text: ${text};`,
    `--brand-section-bg: ${sectionBg};`,
    `--brand-overline: ${overline};`,
    `--brand-font-heading: ${fontHeading};`,
    `--brand-font-body: ${fontBody};`,
    // Derived tokens
    `--brand-text-muted: color-mix(in srgb, var(--brand-text) 75%, var(--brand-background));`,
    `--brand-card-border: color-mix(in srgb, var(--brand-text) 12%, var(--brand-background));`,
    `--brand-card-bg: var(--brand-background);`,
    `--brand-input-border: color-mix(in srgb, var(--brand-text) 55%, var(--brand-background));`,
    `--brand-input-placeholder: color-mix(in srgb, var(--brand-text) 75%, var(--brand-background));`,
    `--brand-surface-subtle: color-mix(in srgb, var(--brand-text) 4%, var(--brand-background));`,
  ];

  return `:root { ${declarations.join(" ")} }`;
}

/** WCAG relative luminance for an opaque six-digit theme color. */
export function contrastRatio(foreground: string, background: string): number {
  const luminance = (hex: string) => {
    const channels = [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16) / 255)
      .map(value => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4);
    return .2126 * channels[0]! + .7152 * channels[1]! + .0722 * channels[2]!;
  };
  const a = luminance(foreground), b = luminance(background);
  return (Math.max(a, b) + .05) / (Math.min(a, b) + .05);
}

/** Rendering-only correction; never rewrites the organization's saved palette. */
export function readableThemeColor(foreground: string, background: string, minimum = 4.5): string {
  if (!/^#[0-9a-f]{6}$/i.test(foreground) || !/^#[0-9a-f]{6}$/i.test(background)) return foreground;
  if (contrastRatio(foreground, background) >= minimum) return foreground;
  const target = contrastRatio('#000000', background) > contrastRatio('#ffffff', background) ? 0 : 255;
  const original = [1, 3, 5].map(index => parseInt(foreground.slice(index, index + 2), 16));
  for (let step = 1; step <= 100; step++) {
    const candidate = '#' + original.map(channel => Math.round(channel + (target - channel) * step / 100).toString(16).padStart(2, '0')).join('');
    if (contrastRatio(candidate, background) >= minimum) return candidate;
  }
  return target === 0 ? '#000000' : '#ffffff';
}
